<template>
  <tr class="energy-stat-row">
    <!-- Fuel -->
    <td class="fuel-name-cell">
      <div class="fuel-name">
        <div class="fuel-icon">
          <v-icon size="18" color="primary">
            {{
              feed
                ? 'mdi-transmission-tower-export'
                : 'mdi-lightning-bolt-outline'
            }}
          </v-icon>
        </div>

        <span>{{ fuel.name }}</span>
      </div>
    </td>

    <!-- Cost / revenue -->
    <td class="energy-value-cell">
      <v-text-field
        :model-value="localCost"
        type="number"
        min="0"
        variant="outlined"
        density="compact"
        hide-details="auto"
        :error-messages="costErrors"
        :aria-label="`${costFieldLabel} für ${fuel.name}`"
        class="energy-input"
        @update:model-value="updateCost"
      />
    </td>

    <!-- Consumption / feed-in -->
    <td class="energy-value-cell">
      <v-text-field
        :model-value="localConsumption"
        type="number"
        min="0"
        variant="outlined"
        density="compact"
        hide-details="auto"
        :error-messages="consumptionErrors"
        :aria-label="`${consumptionFieldLabel} für ${fuel.name}`"
        class="energy-input"
        @update:model-value="updateConsumption"
      />
    </td>

    <!-- Calculated price -->
    <td class="energy-price-cell">
      <div class="calculated-price">
        <span class="price-value">
          {{ chosenEnergyPrice }}
        </span>

        <v-tooltip
          v-if="hint"
          location="bottom"
          max-width="360"
          color="surface" 
          content-class="tooltip"
        >
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              icon
              variant="text"
              size="x-small"
              color="primary"
              aria-label="Hinweis zum berechneten Energiepreis"
            >
              <v-icon size="18">
                mdi-information-outline
              </v-icon>
            </v-btn>
          </template>

          <span>{{ hint }}</span>
        </v-tooltip>
      </div>
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      cost: 0,
      consumption: 0,
    }),
  },

  fuel: {
    type: Object,
    required: true,
  },

  errors: {
    type: Object,
    default: () => ({}),
  },

  hint: {
    type: String,
    default: '',
  },

  feed: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const localCost = computed(() => {
  return props.modelValue?.cost ?? 0
})

const localConsumption = computed(() => {
  return props.modelValue?.consumption ?? 0
})

const chosenEnergyPrice = computed(() => {
  const cost = Number(localCost.value)
  const consumption = Number(localConsumption.value)

  if (
    !Number.isFinite(cost) ||
    !Number.isFinite(consumption) ||
    consumption === 0
  ) {
    return '0,00'
  }

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(cost / consumption)
})

const costErrors = computed(() => {
  return props.errors?.cost || []
})

const consumptionErrors = computed(() => {
  return props.errors?.consumption || []
})

const costFieldLabel = computed(() => {
  return props.feed
    ? 'Einspeiseumsatz'
    : 'Energiekosten'
})

const consumptionFieldLabel = computed(() => {
  return props.feed
    ? 'Einspeiseenergie'
    : 'Energieverbrauch'
})

function normalizeNumber(value) {
  if (value === '' || value === null) {
    return null
  }

  const number = Number(value)

  return Number.isFinite(number)
    ? number
    : value
}

function updateCost(value) {
  emit('update:modelValue', {
    ...props.modelValue,
    cost: normalizeNumber(value),
  })
}

function updateConsumption(value) {
  emit('update:modelValue', {
    ...props.modelValue,
    consumption: normalizeNumber(value),
  })
}
</script>

<style scoped lang="scss">
.energy-stat-row {
  transition: background-color 160ms ease;
}

.energy-stat-row:hover {
  background-color: rgba(var(--v-theme-primary), 0.035);
}

.energy-stat-row td {
  padding: 0.65rem 0.9rem;
  vertical-align: middle;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.07);
}

/* Fuel */
.fuel-name-cell {
  min-width: 190px;
}

.fuel-name {
  display: flex;
  align-items: center;
  gap: 0.6rem;

  font-weight: 600;
}

.fuel-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Editable values */
.energy-value-cell {
  width: 220px;
  min-width: 190px;
}

.energy-input {
  max-width: 220px;
  margin-left: auto;
}

.energy-input :deep(.v-field) {
  border-radius: 14px;
}

.energy-input :deep(input) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Calculated energy price */
.energy-price-cell {
  width: 180px;
  min-width: 160px;
}

.calculated-price {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
}

.price-value {
  min-width: 70px;

  color: rgb(var(--v-theme-on-surface));

  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
</style>